import React, { useState } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { StyledButton } from './styles.tsx';
import { BeatLoader } from 'react-spinners';
import { hapticsImpactLight } from '../../common/haptic.js';
import { useTheme } from 'styled-components';

const bounceAnimation = keyframes`
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(0.94);
  }
  100% {
    transform: scale(1);
  }
`;

const BouncyButton = styled(StyledButton).withConfig({
  shouldForwardProp: (prop) => prop !== '$isAnimating',
})<{ $isAnimating: boolean }>`
  ${({ $isAnimating }) =>
    $isAnimating &&
    css`
      animation: ${bounceAnimation} 0.2s cubic-bezier(0.34, 1.56, 0.64, 1);
    `}
`;

interface ButtonProps {
  children: React.ReactNode;
  onClick?: () => void;
  type?: 'button' | 'submit' | 'reset';
  variant?: 'primary' | 'secondary' | 'white' | 'link' | 'danger';
  fullWidth?: boolean;
  disabled?: boolean;
  loading?: boolean;
  style?: React.CSSProperties;
  as?: React.ElementType;
  clickAnimation?: 'bouncy' | undefined;
  [key: string]: any;
}

const Button: React.FC<ButtonProps> = ({
  children,
  onClick = (e: any) => {},
  type = 'button',
  variant = 'primary',
  fullWidth = false,
  disabled = false,
  loading = false,
  style = {},
  as,
  clickAnimation,
  ...props
}) => {
  const theme = useTheme() as any;
  const [isAnimating, setIsAnimating] = useState(false);
  const isBouncy = clickAnimation === 'bouncy';

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (loading || disabled) return;

    if (isBouncy) {
      hapticsImpactLight();
      setIsAnimating(true);
      setTimeout(() => {
        setIsAnimating(false);
        // Call onClick after animation completes
        onClick(e);
      }, 200);
    } else {
      onClick(e);
    }
  };

  const ButtonComponent = isBouncy ? BouncyButton : StyledButton;

  return (
    <ButtonComponent
      {...(isBouncy ? {} : { as })}
      onClick={handleClick}
      variant={variant}
      disabled={disabled}
      fullWidth={fullWidth}
      type={type}
      $isAnimating={isAnimating}
      style={{
        ...style,
        transformOrigin: isBouncy ? 'center' : undefined,
        boxShadow: isBouncy ? 'none !important' : undefined,
      }}
      $isBouncy={isBouncy}
      {...props}
    >
      {loading ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            zIndex: 1,
          }}
        >
          <BeatLoader
            color={style?.color || theme.background.primary}
            size={8}
            margin={2}
          />
        </div>
      ) : (
        <span style={{ position: 'relative', zIndex: 1, display: 'inline-flex', alignItems: 'center', gap: '8px' }}>{children}</span>
      )}
    </ButtonComponent>
  );
};

export default Button;
